import React from "react";
import { Bar } from "react-chartjs-2";
import "./analytics/analytics.css";
import {
  dayMonthBasedLabel,
  getLabelsFor3Months,
  getLabelsForCurrentMonth,
  getLabelsForCurrentWeek,
  getLabelsForLast12Months,
  getLabelsForLast30Day,
  getLabelsForLast3Months,
  getLabelsForLast6Months,
  getLabelsForLast7Days,
  getLabelsForYear,
  isPastTime,
  isTodayDate,
  monthBasedLabel,
  monthBasedLabelWithYear,
  timeBasedLabel,
  timeLabelForCharts,
} from "./utils/utils";
import "../css/successChart.css";
import dayjs from "dayjs";
import { colorBrandObject } from "../../constants/brandings";

const SalesChart = ({
  chartTransactionOverAll,
  volumeTransactionOverAll,
  selectedDropdownValue,
  locale,
  singleStartDate,
  startDate,
  endDate,
}) => {
  const currencyCode =
    volumeTransactionOverAll?.length > 0
      ? volumeTransactionOverAll[0].currencySign
      : "";

  let labels = [];
  let chartData = [];
  let volumeData = [];
  const currentTime = new Date();
  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();
  const storedState = JSON.parse(
    localStorage.getItem("lastSuccessfulState") || "{}"
  );

  const updateState = (labelsArray, chartDataArray, volumeDataArray) => {
    labels = labelsArray;
    chartData = chartDataArray;
    volumeData = volumeDataArray;
    localStorage.setItem(
      "lastSuccessfulState",
      JSON.stringify({
        labels,
        chartData,
        volumeData,
      })
    );
  };

  const setChartData = ({ labelFunc, labelType, conditionCheck }) => {
    const getLabelData = labelFunc();
    const labels = getLabelData;
    const chartData = [];
    const volumeData = [];

    labels.forEach((dataPoint) => {
      if (conditionCheck) {
        const chartDataItem = chartTransactionOverAll?.find((item) =>
          dataPoint.match(labelType(item?.timestamp))
        );
        chartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : 0
        );

        const volumeDataItem = volumeTransactionOverAll?.find((item) =>
          dataPoint.match(labelType(item?.timestamp))
        );
        volumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      } else {
        chartData.push(null);
        volumeData.push(null);
      }

    });

    updateState(labels, chartData, volumeData);
  }

  switch (selectedDropdownValue) {
    case "yesterday":
      const getLabelForYesterday = timeLabelForCharts();
      const yesterdayLabels = getLabelForYesterday;
      const yesterdayChartData = [];
      const yesterdayVolumeData = [];

      yesterdayLabels.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find((item) =>
          dataPoint.match(timeBasedLabel(item?.timestamp))
        );
        yesterdayChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : 0
        );

        const volumeDataItem = volumeTransactionOverAll?.find((item) =>
          dataPoint.match(timeBasedLabel(item?.timestamp))
        );
        yesterdayVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(yesterdayLabels, yesterdayChartData, yesterdayVolumeData);
      break;

    case "today":
      const getLabelForToday = timeLabelForCharts();
      const todayLabels = getLabelForToday;
      const todayChartData = [];
      const todayVolumeData = [];

      todayLabels.forEach((dataPoint) => {
        const pastTime = isPastTime(dataPoint, currentHour, currentMinute);
        if (pastTime) {
          const chartDataItem = chartTransactionOverAll?.find((item) =>
            dataPoint.match(timeBasedLabel(item?.timestamp))
          );
          todayChartData.push(
            chartDataItem ? chartDataItem["successTransactionsCount"] : 0
          );

          const volumeDataItem = volumeTransactionOverAll?.find((item) =>
            dataPoint.match(timeBasedLabel(item?.timestamp))
          );
          todayVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
        } else {
          todayChartData.push(null);
          todayVolumeData.push(null);
        }
      });

      updateState(todayLabels, todayChartData, todayVolumeData);
      break;

    case "thisWeek":
      const getLabelForCurrentWeek = getLabelsForCurrentWeek();
      const thisWeekLabels = getLabelForCurrentWeek;
      const thisWeekChartData = [];
      const thisWeekVolumeData = [];

      thisWeekLabels.forEach((dataPoint) => {
        const dataPointDate = dayjs(dataPoint, "DD MMM");
        if (dataPointDate.isBefore(dayjs(), "day") || dataPointDate.isSame(dayjs(), "day")
        ) {
          const chartDataItem = chartTransactionOverAll.find(
            (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
          );
          thisWeekChartData.push(
            chartDataItem ? chartDataItem["successTransactionsCount"] : 0
          );

          const volumeDataItem = volumeTransactionOverAll.find(
            (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
          );
          thisWeekVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
        } else {
          thisWeekChartData.push(null);
          thisWeekVolumeData.push(null);
        }
      });

      updateState(thisWeekLabels, thisWeekChartData, thisWeekVolumeData);
      break;

    case "lastWeek":
      const getLabelForLastWeek = getLabelsForLast7Days();
      const lastWeekLabels = getLabelForLastWeek;
      const lastWeekChartData = [];
      const lastWeekVolumeData = [];

      lastWeekLabels.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll.find(
          (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
        );
        lastWeekChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : 0
        );

        const volumeDataItem = volumeTransactionOverAll.find(
          (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
        );
        lastWeekVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(lastWeekLabels, lastWeekChartData, lastWeekVolumeData);
      break;

    case "thisLastMonth":
      const getLabelForLastMonth = getLabelsForCurrentMonth();
      const thisLastMonthLabels = getLabelForLastMonth;
      const thisLastMonthChartData = [];
      const thisLastMonthVolumeData = [];

      thisLastMonthLabels.forEach((dataPoint) => {
        const dataPointDate = dayjs(dataPoint, "DD MMM");
        if (
          dataPointDate.isBefore(dayjs(), "day") ||
          dataPointDate.isSame(dayjs(), "day")
        ) {
          const chartDataItem = chartTransactionOverAll.find(
            (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
          );
          thisLastMonthChartData.push(
            chartDataItem ? chartDataItem["successTransactionsCount"] : 0
          );

          const volumeDataItem = volumeTransactionOverAll.find(
            (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
          );
          thisLastMonthVolumeData.push(
            volumeDataItem ? volumeDataItem?.gmv : 0
          );
        } else {
          thisLastMonthChartData.push(null);
          thisLastMonthVolumeData.push(null);
        }
      });

      updateState(
        thisLastMonthLabels,
        thisLastMonthChartData,
        thisLastMonthVolumeData
      );
      break;

    case "thisMonth":
      const getLabelForMonth = getLabelsForLast30Day();
      const thisMonthLabels = getLabelForMonth;
      const thisMonthChartData = [];
      const thisMonthVolumeData = [];

      thisMonthLabels.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find(
          (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
        );
        thisMonthChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : 0
        );

        const volumeDataItem = volumeTransactionOverAll?.find(
          (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
        );
        thisMonthVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(thisMonthLabels, thisMonthChartData, thisMonthVolumeData);
      break;

    case "thisYear":
      const getLabelforYear = getLabelsForYear();
      const thisYearLabels = getLabelforYear;
      const thisYearChartData = [];
      const thisYearVolumeData = [];

      thisYearLabels.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabel(item?.timestamp)
        );
        thisYearChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : null
        );

        const volumeDataItem = volumeTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabel(item?.timestamp)
        );
        thisYearVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(thisYearLabels, thisYearChartData, thisYearVolumeData);
      break;

    case "last3Months":
      const getLabelForLast3Months = getLabelsForLast3Months();
      const last3MonthsLabel = getLabelForLast3Months;
      const last3MonthsChartData = [];
      const last3MonthsData = [];

      last3MonthsLabel.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find((item) => {
          return dataPoint === monthBasedLabelWithYear(item?.timestamp)
        });
        last3MonthsChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : null
        );

        const volumeDataItem = volumeTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabelWithYear(item?.timestamp)
        );
        last3MonthsData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(last3MonthsLabel, last3MonthsChartData, last3MonthsData);
      break;


    case "last6Months":
      const getLabelForLast6Months = getLabelsForLast6Months();
      const last6MonthsLabel = getLabelForLast6Months;
      const last6MonthsChartData = [];
      const last6MonthsData = [];

      last6MonthsLabel.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabelWithYear(item?.timestamp)
        );
        last6MonthsChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : null
        );

        const volumeDataItem = volumeTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabelWithYear(item?.timestamp)
        );
        last6MonthsData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(last6MonthsLabel, last6MonthsChartData, last6MonthsData);
      break;

    case "last12Months":
      const getLabelForLast12Months = getLabelsForLast12Months();
      const last12MonthsLabel = getLabelForLast12Months;
      const last12MonthsChartData = [];
      const last12MonthsData = [];

      last12MonthsLabel.forEach((dataPoint) => {
        const chartDataItem = chartTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabelWithYear(item?.timestamp)
        );
        last12MonthsChartData.push(
          chartDataItem ? chartDataItem["successTransactionsCount"] : null
        );

        const volumeDataItem = volumeTransactionOverAll?.find(
          (item) => dataPoint === monthBasedLabelWithYear(item?.timestamp)
        );
        last12MonthsData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
      });

      updateState(last12MonthsLabel, last12MonthsChartData, last12MonthsData);
      break;


    case "customDate":
      const customChartData = [];
      const customVolumeData = [];
      if (singleStartDate) {
        const singleStartDateObj = dayjs(singleStartDate);
        const useCurrentTime = isTodayDate(singleStartDate);

        const customLabels = timeLabelForCharts(); // Initialize labels for the singleStartDate case

        customLabels.forEach((dataPoint) => {
          const comparisonTime = useCurrentTime ? dayjs() : singleStartDateObj.endOf("day");
          const pastTime = isPastTime(
            dataPoint,
            comparisonTime.hour(),
            comparisonTime.minute()
          );

          if (pastTime) {
            const chartDataItem = chartTransactionOverAll?.find((item) =>
              dataPoint.match(timeBasedLabel(item?.timestamp))
            );
            customChartData.push(
              chartDataItem ? chartDataItem["successTransactionsCount"] : 0
            );

            const volumeDataItem = volumeTransactionOverAll?.find((item) =>
              dataPoint.match(timeBasedLabel(item?.timestamp))
            );
            customVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
          } else {
            customChartData.push(null);
            customVolumeData.push(null);
          }
        });

        updateState(customLabels, customChartData, customVolumeData);
      } else if (startDate && endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diffInDays = end.diff(start, "day");
        const isCurrentDate = dayjs().isSame(start, "day");

        if (start.isSame(end, "day")) {
          // If startDate and endDate are the same
          const customLabels = timeLabelForCharts();

          customLabels.forEach((dataPoint) => {
            const comparisonTime = isCurrentDate ? dayjs() : start.endOf("day");
            const pastTime = isPastTime(
              dataPoint,
              comparisonTime.hour(),
              comparisonTime.minute()
            );

            if (pastTime) {
              const chartDataItem = chartTransactionOverAll?.find((item) =>
                dataPoint.match(timeBasedLabel(item?.timestamp))
              );
              customChartData.push(
                chartDataItem ? chartDataItem["successTransactionsCount"] : 0
              );

              const volumeDataItem = volumeTransactionOverAll?.find((item) =>
                dataPoint.match(timeBasedLabel(item?.timestamp))
              );
              customVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
            } else {
              customChartData.push(null);
              customVolumeData.push(null);
            }
          });

          updateState(customLabels, customChartData, customVolumeData);
        } else if (diffInDays <= 62) {
          // If difference is less than or equal to 62 days, show daily labels
          const customLabels = [];
          for (let i = 0; i <= diffInDays; i++) {
            customLabels.push(start.add(i, "day").format("DD MMM"));
          }

          customLabels.forEach((dataPoint) => {
            const chartDataItem = chartTransactionOverAll?.find(
              (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
            );
            customChartData.push(
              chartDataItem ? chartDataItem["successTransactionsCount"] : 0
            );

            const volumeDataItem = volumeTransactionOverAll?.find(
              (item) => dataPoint === dayMonthBasedLabel(item?.timestamp)
            );
            customVolumeData.push(volumeDataItem ? volumeDataItem?.gmv : 0);
          });

          updateState(customLabels, customChartData, customVolumeData);
        }
      } else {
        if (
          storedState.labels &&
          storedState.chartData &&
          storedState.volumeData
        ) {
          updateState(
            storedState.labels,
            storedState.chartData,
            storedState.volumeData
          );
        }
      }
      break;
  }

  const data = {
    labels: labels,
    datasets: [
      {
        type: "line",
        label: "Successful Transactions",
        data: chartData,
        fill: false,
        backgroundColor: colorBrandObject?.lineGraphColor,
        borderColor: colorBrandObject?.lineGraphColor,
        hoverBackgroundColor: colorBrandObject?.lineGraphColor,
        hoverBorderColor: colorBrandObject?.lineGraphColor,
        yAxisID: "y",
        order: 1,
      },
      {
        label: "Transactional Value",
        data: volumeData,
        type: "bar",
        fill: false,
        borderColor: colorBrandObject?.barGraphColor,
        backgroundColor: colorBrandObject?.barGraphColor,
        pointBorderColor: colorBrandObject?.barGraphColor,
        pointBackgroundColor: colorBrandObject?.barGraphColor,
        pointHoverBackgroundColor: colorBrandObject?.barGraphColor,
        pointHoverBorderColor: colorBrandObject?.barGraphColor,
        yAxisID: "y1",
        order: 2,
      },
    ],
  };

  const options = {
    font: {
      family: "Poppins",
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'category',  // Explicitly set the x-axis scale type to 'category'
        title: {
          display: true,
          text: "",
          color: "#6c6c6c",
          font: {
            family: "Poppins",
            size: "12px",
            fontSize: "12px",
            weight: "400",
          },
        },
        display: true,
        offset: true,
        grid: {
          display: false,
        },
        labels: data.labels,
      },
      y1: {
        beginAtZero: true,
        grid: {
          borderDash: [8, 6],
        },
        title: {
          display: true,
          text: "Value",
          color: "#6c6c6c",
          font: {
            family: "Poppins",
            size: "12px",
            fontSize: "12px",
            style: "normal",
            weight: "400",
          },
        },
        type: "linear",
        display: true,
        position: "left",
        ticks: {
          callback: (value) => {
            return (
              currencyCode +
              " " +
              new Intl.NumberFormat(locale?.replace("_", "-"), {
                notation: "compact",
                compactDisplay: "short",
              })
                .format(value)
                .replace("T", "K")
            );
          },
        },
        suggestedMax: Math.max(...volumeData) * 1.15,
      },
      y: {
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: "Count",
          color: "var(--text-medium, rgba(1, 1, 2, 0.60))",
          font: {
            family: "Poppins",
            size: "12px",
            fontSize: "12px",
            style: "normal",
            weight: "400",
          },
        },
        type: "linear",
        display: true,
        position: "right",
        precision: 0,
        ticks: {
          callback: (value) => {
            return new Intl.NumberFormat(locale?.replace("_", "-"), {
              notation: "compact",
              compactDisplay: "short",
            })
              .format(value)
              .replace("T", "K");
          },
        },
        suggestedMax: Math.max(...chartData) * 1.15,
      },
    },
    plugins: {
      legend: {
        layout: {
          padding: 100,
        },
        position: "top",
        labels: {
          usePointStyle: true,
        },
      },
    },
  };

  return (
    <div style={{ height: "350px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <p className="sales-chart-header">Sales </p>
      </div>

      <Bar data={data} options={options} />
    </div>
  );
};

export default SalesChart;
